import { ErrorMessage } from "@hookform/error-message";
import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import { NumericFormat, numericFormatter } from "react-number-format";
import {
  Col,
  Row,
  RSelect
} from "../../../components/Component";
import { GET } from "../../../helper/api";
import { CURRENCY_CODE, CURRENCY_CODE_OPTIONS } from "../../../utils/Utils";
import moment from "moment";
import { formatBankAccountWithBalanceOptions } from "../../../helper/bank-account";
import { buildLabelMortgageContract } from "../../../helper/mortgage-contract";



const UpsertPNTransactionHistoryComponent = ({
  type,
  defaultMortgageContractOptions,
  disabledForm
}) => {

  const [categoryOptions, setCategoryOptions] = useState([]);
  const [bankAccountOptions, setBankAccountOptions] = useState([]);
  const [loanContractOptions, setLoanContractOptions] = useState([]);

  const { errors, register, control, setValue, watch } = useFormContext();

  useEffect(() => {
    if (!defaultMortgageContractOptions?.length) {
      return;
    }
    setLoanContractOptions(oldData => {
      const newData = [...oldData];
      const values = oldData.map((e) => e.value);
      defaultMortgageContractOptions.forEach(defaultMortgageContractOption => {
        const { value } = defaultMortgageContractOption;
        if (!values.includes(value)) {
          newData.push({
            ...defaultMortgageContractOption,
          });
        }
      })

      return newData;
    })
  }, [defaultMortgageContractOptions])

  useEffect(() => {
    GET("/pn-category", { type }, true).then(({ data }) => {
      setCategoryOptions([
        ...data?.map((c) => ({
          label: `${c.name}${c.type === "IN" ? "(Nhập)" : "(Xuất)"}`,
          value: c.id,
          data: c,
        })),
      ]);
    });

    GET("/pn-bank-account/find-all-with-balance", {}, true).then(({ data }) => {
      setBankAccountOptions(formatBankAccountWithBalanceOptions(data));
    });

    GET(
      "/transaction-history/mortgage-contract",
      { status: "INIT" },
      true
    ).then(({ data }) => {
      setLoanContractOptions((oldData) => {
        const newData = [...oldData];
        const values = oldData.map((e) => e.value);
        data?.forEach((c) => {
          if (!values.includes(c.contractId)) {
            newData.push({
              label: buildLabelMortgageContract(c),
              value: c.contractId,
              data: c,
            });
          }
        });
        return newData;
      });
    });
  }, []);

  const contractId = watch("contractId");
  const contractSelected = useMemo(() => {
    if (!contractId) {
      return null;
    }
    return loanContractOptions.find((e) => e.value === contractId)?.data;
  }, [contractId, loanContractOptions]);



  const fundUSD = watch("fundUSD");
  const fund = watch("fund");
  const exchangeInterestRate = watch("exchangeInterestRate");
  const interestUSD = watch("interestUSD");
  const interest = watch("interest");

  const currency = useWatch({
    control,
    name: "currency",
  });

  const exchangeRate = useWatch({
    control,
    name: "exchangeRate",
  });

  const amountUSD = useWatch({
    control,
    name: "amountUSD",
  });

  const isUSD = useMemo(
    () => currency === 'USD',
    [currency]
  );


  useEffect(() => {
    if (contractSelected) {
      return;
    }
    if (currency === 'VND') {
      setValue('amountUSD', '');
      setValue('exchangeRate', '');
      return;
    }

    if (currency === 'USD') {
      setValue('amount', (amountUSD && exchangeRate) ? Math.round(amountUSD * exchangeRate) : '')
      return;
    }
  }, [currency, amountUSD, exchangeRate, contractSelected, contractId])


  useEffect(() => {
    if (!contractSelected) {
      return;
    }
    if (!isUSD) {
      return;
    }
    const fund = (fundUSD * contractSelected.exchangeRate) || '';
    const interest = (interestUSD * exchangeInterestRate) || '';
    setValue('fund', fund);
    setValue('interest', interest);
    setValue('amount', (fund || 0) + (interest || 0));
  }, [interestUSD, fundUSD, exchangeInterestRate, isUSD, contractSelected, contractId, setValue]);

  useEffect(() => {
    if (!contractSelected) {
      return;
    }
    if (isUSD) {
      return;
    }
    setValue('interestUSD', '');
    setValue('fundUSD', '');
    setValue('exchangeInterestRate', '');
    setValue('amount', (fund || 0) + (interest || 0));
  }, [fund, interest, contractSelected, contractId, setValue]);

  return (
    <div className="position-relative">
      {disabledForm ? <div className="form-overlay"></div> : <></>}
      <Row className="g-3">
        <Col md="6">
          <div className="form-group">
            <label className="form-label" htmlFor="customer">
              Ngày <span className="text-danger">*</span>
            </label>
            <div className="form-control-wrap">
              <input
                type="date"
                className="form-control"
                name="date"
                max={moment().format("YYYY-MM-DD")}

                ref={register({
                  required: "Vui lòng nhập thông tin",
                })}
              />
              <ErrorMessage
                errors={errors}
                name="date"
                render={({ message }) => (
                  <span className="invalid">{message}</span>
                )}
              />
            </div>
          </div>
        </Col>
        <Col md="6">
          <div className="form-group">
            <label className="form-label" >
              Tiền tệ
            </label>
            <div className="form-control-wrap">
              <Controller
                control={control}
                name="currency"
                render={({ onChange, value, name, ref }) => (
                  <RSelect
                    isDisabled={!!contractId}
                    ref={ref}
                    name={name}
                    options={CURRENCY_CODE_OPTIONS} value={CURRENCY_CODE_OPTIONS.find((c) => c.value === value)} onChange={(val) => onChange(val.value)} />
                )}
              />
            </div>
          </div>
        </Col>
        {!contractId && isUSD ? (
          <>
            <Col md="6">
              <div className="form-group">
                <label className="form-label" >
                  Đơn giá đô<span className="text-danger">*</span>
                </label>
                <div className="form-control-wrap">
                  <Controller
                    control={control}
                    name="amountUSD"
                    rules={{ required: "Vui lòng nhập thông tin" }}
                    render={({ onChange, value, ref }) => (
                      <NumericFormat
                        className="form-control"
                        getInputRef={ref}
                        type="text"
                        value={value}
                        thousandsGroupStyle="thousand"
                        thousandSeparator=","
                        decimalScale={4}
                        onValueChange={(val) => onChange(val.floatValue)}
                      />
                    )}
                  />
                  <ErrorMessage errors={errors} name="amountUSD" render={({ message }) => <span className="invalid">{message}</span>} />
                </div>
              </div>
            </Col>
          </>
        ) : (
          <></>
        )}
        {isUSD ? <>
          <Col md="6">
            <div className="form-group">
              <label className="form-label" >
                Tỉ giá<span className="text-danger">*</span>
              </label>
              <div className="form-control-wrap">
                <Controller
                  control={control}
                  name="exchangeRate"
                  rules={{ required: "Vui lòng nhập thông tin" }}
                  render={({ onChange, value, ref }) => (
                    <NumericFormat
                      className="form-control"
                      getInputRef={ref}
                      type="text"
                      value={value}
                      thousandsGroupStyle="thousand"
                      thousandSeparator=","
                      decimalScale={2}
                      onValueChange={(val) => onChange(val.floatValue)}
                    />
                  )}
                />
                <ErrorMessage errors={errors} name="exchangeRate" render={({ message }) => <span className="invalid">{message}</span>} />
              </div>
            </div>
          </Col>
        </> : <></>}
        <Col md="6">
          <div className="form-group">
            <label className="form-label" >
              Số tiền(đ)
              <span className="text-danger">*</span>
            </label>
            <div className="form-control-wrap">
              <Controller
                control={control}
                name="amount"
                rules={{ required: "Vui lòng nhập thông tin" }}
                render={({ onChange, value, ref }) => (
                  <NumericFormat
                    className="form-control"
                    inputRef={ref}
                    type="text"
                    value={value}
                    disabled={contractId || isUSD ? true : false}
                    thousandsGroupStyle="thousand"
                    thousandSeparator=","
                    decimalScale={2}
                    onValueChange={(val) =>
                      onChange(val.floatValue)
                    }
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="amount"
                render={({ message }) => (
                  <span className="invalid">{message}</span>
                )}
              />
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="form-group">
            <label className="form-label" >
              Nội dung
              <span className="text-danger">*</span>
            </label>
            <div className="form-control-wrap">
              <Controller
                control={control}
                name="content"
                rules={{ required: "Vui lòng nhập thông tin" }}
                render={({ onChange, value, ref }) => (
                  <textarea
                    className="form-control"
                    inputRef={ref}
                    value={value}
                    onChange={(e) => onChange(e.target.value)}
                  ></textarea>
                )}
              />
              <ErrorMessage
                errors={errors}
                name="content"
                render={({ message }) => (
                  <span className="invalid">{message}</span>
                )}
              />
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="form-group">
            <label className="form-label" >
              Loại<span className="text-danger">*</span>
            </label>
            <div className="form-control-wrap">
              <Controller
                control={control}
                name="categoryId"
                rules={{ required: "Vui lòng nhập thông tin" }}
                render={({ onChange, value, name, ref }) => (
                  <RSelect
                    inputRef={ref}
                    name={name}
                    options={categoryOptions}
                    value={categoryOptions.find(
                      (c) => c.value === value
                    )}
                    onChange={(val) => onChange(val.value)}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="categoryId"
                render={({ message }) => (
                  <span className="invalid">{message}</span>
                )}
              />
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="form-group">
            <label className="form-label" >
              Quỹ tài khoản<span className="text-danger">*</span>
            </label>
            <div className="form-control-wrap">
              <Controller
                control={control}
                name="bankAccountId"
                rules={{ required: "Vui lòng nhập thông tin" }}
                render={({ onChange, value, name, ref }) => (
                  <RSelect
                    inputRef={ref}
                    name={name}
                    options={bankAccountOptions}
                    value={bankAccountOptions.find(
                      (c) => c.value === value
                    )}
                    onChange={(val) => onChange(val.value)}
                  />
                )}
              />
              <ErrorMessage
                errors={errors}
                name="bankAccountId"
                render={({ message }) => (
                  <span className="invalid">{message}</span>
                )}
              />
            </div>
          </div>
        </Col>
        <Col md="6">
          <div className="form-group">
            <label className="form-label" >
              Người nhận
            </label>
            <div className="form-control-wrap">
              <input
                type="text"
                className="form-control"
                name="receiver"
                ref={register()}
              />
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="form-group">
            <label className="form-label" >
              Địa chỉ
            </label>
            <div className="form-control-wrap">
              <input
                type="text"
                className="form-control"
                name="address"
                ref={register()}
              />
            </div>
          </div>
        </Col>

        <Col md="6">
          <div className="form-group">
            <label className="form-label" >
              Mã hợp đồng
            </label>
            <div className="form-control-wrap">
              <Controller
                control={control}
                name="contractId"
                render={({ onChange, value, name, ref }) => (
                  <RSelect
                    isClearable
                    inputRef={ref}
                    name={name}
                    options={loanContractOptions}
                    value={loanContractOptions.find(
                      (c) => c.value === value
                    )}
                    onChange={(val) => {
                      setValue("interestUSD", "");
                      setValue("fundUSD", "");
                      setValue("exchangeRate", "");
                      setValue("exchangeInterestRate", "");
                      setValue("fund", "");
                      setValue("interest", "");
                      setValue("amount", "");
                      setValue("currency", CURRENCY_CODE.VND);
                      onChange(val?.value);
                      if (val?.value) {
                        setValue("currency", val?.data?.currency);
                        setValue("exchangeRate", val?.data?.exchangeRate);
                      }
                    }}
                  />
                )}
              />
            </div>
          </div>
        </Col>

        <Col md="6" className={'d-none'}>
          <div className="form-group">
            <label className="form-label" >
              Giao dich NH
            </label>
            <div className="form-control-wrap">
              <input
                type="text"
                className="form-control"
                name="pnHookTransactionHistoryId"
                ref={register()}
              />
            </div>
          </div>
        </Col>


        {contractId ? (
          <>
            {isUSD ? (
              <>
                {type === 'OUT' ? <><Col md="6">
                  <div className="form-group">
                    <label
                      className="form-label"

                    >
                      Tỷ giá tiền lãi
                      <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="exchangeInterestRate"
                        rules={{
                          required: "Vui lòng nhập thông tin",
                        }}
                        render={({ onChange, value, ref }) => (
                          <NumericFormat
                            className="form-control"
                            inputRef={ref}
                            type="text"
                            value={value}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            decimalScale={2}
                            onValueChange={(val) => {
                              onChange(val.floatValue);
                            }}
                          />
                        )}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="exchangeInterestRate"
                        render={({ message }) => (
                          <span className="invalid">
                            {message}
                          </span>
                        )}
                      />
                    </div>
                  </div>
                </Col>
                  <Col md="6">
                    <div className="form-group">
                      <label
                        className="form-label"

                      >
                        Tiền lãi
                        <span className="text-danger">*</span>
                      </label>
                      <div className="form-control-wrap">
                        <Controller
                          control={control}
                          name="interestUSD"
                          rules={{
                            required: "Vui lòng nhập thông tin",
                          }}
                          render={({ onChange, value, ref }) => (
                            <NumericFormat
                              className="form-control"
                              inputRef={ref}
                              type="text"
                              value={value}
                              thousandsGroupStyle="thousand"
                              thousandSeparator=","
                              decimalScale={4}
                              onValueChange={(val) => {
                                onChange(val.floatValue);
                              }}
                            />
                          )}
                        />
                        <ErrorMessage
                          errors={errors}
                          name="interestUSD"
                          render={({ message }) => (
                            <span className="invalid">
                              {message}
                            </span>
                          )}
                        />
                      </div>
                    </div>
                  </Col></> : <></>}
                <Col md="6">
                  <div className="form-group">
                    <label
                      className="form-label"

                    >
                      Tiền gốc($)
                      <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="fundUSD"
                        rules={{
                          required: "Vui lòng nhập thông tin",
                        }}
                        render={({ onChange, value, ref }) => (
                          <NumericFormat
                            className="form-control"
                            inputRef={ref}
                            type="text"
                            value={value}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            decimalScale={4}
                            onValueChange={(val) => {
                              onChange(val.floatValue);
                            }}
                          />
                        )}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="fundUSD"
                        render={({ message }) => (
                          <span className="invalid">
                            {message}
                          </span>
                        )}
                      />
                    </div>
                  </div>
                </Col>

              </>
            ) : (
              <>
                <Col md="6">
                  <div className="form-group">
                    <label
                      className="form-label"

                    >
                      Tiền gốc(đ)
                      <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="fund"
                        rules={{
                          required: "Vui lòng nhập thông tin",
                        }}
                        render={({ onChange, value, ref }) => (
                          <NumericFormat
                            className="form-control"
                            inputRef={ref}
                            type="text"
                            value={value}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            decimalScale={2}
                            onValueChange={(val) => {
                              onChange(val.floatValue);
                            }}
                          />
                        )}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="fund"
                        render={({ message }) => (
                          <span className="invalid">
                            {message}
                          </span>
                        )}
                      />
                    </div>
                  </div>
                </Col>
                {type === 'OUT' ? <Col md="6">
                  <div className="form-group">
                    <label
                      className="form-label"

                    >
                      Tiền lãi(đ)
                      <span className="text-danger">*</span>
                    </label>
                    <div className="form-control-wrap">
                      <Controller
                        control={control}
                        name="interest"
                        rules={{
                          required: "Vui lòng nhập thông tin",
                        }}
                        render={({ onChange, value, ref }) => (
                          <NumericFormat
                            className="form-control"
                            inputRef={ref}
                            type="text"
                            value={value}
                            thousandsGroupStyle="thousand"
                            thousandSeparator=","
                            decimalScale={2}
                            onValueChange={(val) => {
                              onChange(val.floatValue);
                            }}
                          />
                        )}
                      />
                      <ErrorMessage
                        errors={errors}
                        name="interest"
                        render={({ message }) => (
                          <span className="invalid">
                            {message}
                          </span>
                        )}
                      />
                    </div>
                  </div>
                </Col> : <></>}
              </>
            )}
          </>
        ) : (
          <></>
        )}
      </Row>
    </div>
  )
}

export default UpsertPNTransactionHistoryComponent;